/**
 * The reload-safety gate — the page's ONE answer to "may this page reload now?" (WEB-APP-VERSION-REFRESH-001).
 *
 * A new build is picked up by reloading, and a reload at the wrong moment destroys work: pos-web's cart lives in
 * memory, so a reload mid-sale loses the sale; a reload with a form dirty loses the edits; a reload while a save is on
 * the wire leaves the person not knowing whether it happened. So the page keeps a registry of reasons not to reload:
 *
 * - `unsavedChanges` — registered by page-shell's `useBeforeUnloadWarning`, the atom every dirty-form primitive
 *   builds on (`useUnsavedChangesGuard`, the wizard leave guard), so every adopter blocks a reload for free;
 * - `saleInProgress` — registered by a point-of-sale screen while a sale is open;
 * - `signInInProgress` — registered by login-shell's form while a password is typed (a sign-in screen stands open on a
 *   register for days, and a reload would erase the password under the person typing it);
 * - `requestInFlight` — derived from this package's request tracker, which the facade feeds for every request a
 *   person is waiting on (never a background poll).
 *
 * The gate is open when the registry is empty. Every reload a new build asks for goes through `reloadWhenSafe`, which
 * reloads only when open. It lives here, below both shells, because the notice that reads it is mounted by page-shell's
 * `<AppShell>` AND auth-shell's `<AuthLayout>`, and page-shell depends on auth-shell.
 *
 * @module @puffin-core/api-client/reloadSafetyGate
 */

import { getPageRequestTracker } from './requestTracker.js';

/** Why a page may not reload now, in the order the notice prefers to name them. */
export const RELOAD_BLOCK_REASON = Object.freeze({
  UNSAVED_CHANGES: 'unsavedChanges',
  SALE_IN_PROGRESS: 'saleInProgress',
  SIGN_IN_IN_PROGRESS: 'signInInProgress',
  REQUEST_IN_FLIGHT: 'requestInFlight',
});

const KNOWN_REASONS = new Set(Object.values(RELOAD_BLOCK_REASON));

/**
 * The page's own reload — the ONE call site that reloads for a new build. With no page (Node, a worker) there is
 * nothing to reload, and nothing to crash on.
 */
function reloadThePage() {
  globalThis.location?.reload();
}

/**
 * @param {Object} options
 * @param {{getInFlight: () => number, subscribe: Function}} options.requestTracker - requests a person waits on
 * @param {() => void} [options.reload] - reloads the page
 * @returns {Readonly<{
 *   block: (reason: string) => () => void,
 *   getState: () => Readonly<{open: boolean, blockers: ReadonlyArray<string>}>,
 *   subscribe: (listener: Function) => () => void,
 *   reloadWhenSafe: () => boolean,
 * }>}
 */
export function createReloadSafetyGate({ requestTracker, reload = reloadThePage }) {
  // Each registration is its own entry, so two dirty forms need two releases.
  const registrations = [];
  const listeners = new Set();
  let snapshot = null;

  const computeState = () => {
    const blockers = [];
    for (const { reason } of registrations) {
      if (!blockers.includes(reason)) blockers.push(reason);
    }
    if (requestTracker.getInFlight() > 0) blockers.push(RELOAD_BLOCK_REASON.REQUEST_IN_FLIGHT);
    return Object.freeze({ open: blockers.length === 0, blockers: Object.freeze(blockers) });
  };

  const changed = () => {
    snapshot = null;
    for (const listener of listeners) listener();
  };

  requestTracker.subscribe(changed);

  const getState = () => {
    if (snapshot === null) snapshot = computeState();
    return snapshot;
  };

  return Object.freeze({
    /**
     * Hold the gate shut for `reason`.
     *
     * @param {string} reason - one of {@link RELOAD_BLOCK_REASON}
     * @returns {() => void} the release — idempotent
     * @throws {TypeError} on a reason the gate does not know
     */
    block(reason) {
      if (!KNOWN_REASONS.has(reason) || reason === RELOAD_BLOCK_REASON.REQUEST_IN_FLIGHT) {
        throw new TypeError(`reloadSafetyGate.block: unknown reason "${reason}".`);
      }
      const registration = { reason };
      registrations.push(registration);
      changed();
      return () => {
        const index = registrations.indexOf(registration);
        if (index === -1) return;
        registrations.splice(index, 1);
        changed();
      };
    },

    getState,

    /**
     * @param {() => void} listener - told after every change
     * @returns {() => void} unsubscribe
     * @throws {TypeError} when `listener` is not a function
     */
    subscribe(listener) {
      if (typeof listener !== 'function') {
        throw new TypeError('reloadSafetyGate.subscribe: `listener` must be a function.');
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Reload the page if, and only if, it is safe now.
     *
     * @returns {boolean} whether the page is reloading
     */
    reloadWhenSafe() {
      if (!getState().open) return false;
      reload();
      return true;
    },
  });
}

let pageGate = null;

/**
 * The page's gate — one per document, over the page's request tracker.
 *
 * @returns {ReturnType<typeof createReloadSafetyGate>}
 */
export function getPageReloadSafetyGate() {
  if (!pageGate) pageGate = createReloadSafetyGate({ requestTracker: getPageRequestTracker() });
  return pageGate;
}
